import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Avatar, Box, Button, Chip, Divider, Drawer, IconButton, InputAdornment, Paper, Skeleton, Tab, Tabs,
  TextField, Tooltip, Typography, useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip } from "recharts";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import { tokens } from "../../../theme";
import { MEMBER_STATUS_META } from "../attendance/zkStatusStyles";
import { membershipStatusLabel } from "../hpcAdminStatusStyles";
import { PALETTE, requestError, titleCase } from "./overviewUtils";

/** Theme-aware surface colours shared by every overview panel (dark and light mode). */
export const useSurface = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    colors,
    card: dark ? colors.primary[400] : "#ffffff",
    border: dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.12)",
    text: theme.palette.text.primary,
    muted: dark ? "#b8c5d6" : "#5b6474",
    grid: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
    axis: dark ? "#9aa7b8" : "#5b6474",
    hover: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
  };
};

/** "ready" | "loading" | "error" | "unavailable" | "hidden" for one API section. */
export const sectionState = (section, failed) => {
  if (!section) return failed ? "error" : "loading";
  if (section.available === false) return section.reason === "module_disabled" ? "hidden" : "unavailable";
  return "ready";
};

const UNAVAILABLE_TEXT = {
  table_missing: "Not set up yet.",
  query_failed: "Could not load. Try Refresh.",
};

/** ⓘ icon with a one-line explanation. */
export function InfoTip({ text }) {
  const s = useSurface();
  if (!text) return null;
  return (
    <Tooltip title={text} arrow>
      <InfoOutlinedIcon tabIndex={0} aria-label={text} sx={{ fontSize: 16, color: s.muted, cursor: "help" }} />
    </Tooltip>
  );
}

export function Panel({ title, subtitle, info, icon, action, children, id, sx }) {
  const s = useSurface();
  return (
    <Paper
      id={id}
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 }, height: "100%", minWidth: 0, borderRadius: 2,
        backgroundColor: s.card, border: `1px solid ${s.border}`,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", minWidth: 0, flex: "1 1 220px" }}>
          {icon}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: s.text, lineHeight: 1.3 }}>{title}</Typography>
              <InfoTip text={info} />
            </Box>
            {subtitle && <Typography sx={{ fontSize: 13, color: s.muted, mt: 0.25 }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {action && <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>{action}</Box>}
      </Box>
      {children}
    </Paper>
  );
}

export function PanelState({ state, reason, empty, emptyText, height = 220, children }) {
  const s = useSurface();
  if (state === "loading") return <Skeleton variant="rounded" height={height} />;
  if (state === "error") {
    return <Alert severity="error" variant="outlined" sx={{ fontSize: 14 }}>Could not load this section.</Alert>;
  }
  if (state === "unavailable") {
    return <Alert severity="info" variant="outlined" sx={{ fontSize: 14 }}>{UNAVAILABLE_TEXT[reason] || "Not available."}</Alert>;
  }
  if (empty) {
    return (
      <Box sx={{ minHeight: Math.min(height, 160), display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 1, py: 3, textAlign: "center" }}>
        <InboxOutlinedIcon sx={{ color: s.muted, fontSize: 32 }} aria-hidden />
        <Typography sx={{ fontSize: 14, color: s.muted }}>{emptyText}</Typography>
      </Box>
    );
  }
  return children;
}

export function IconBubble({ Icon, color, size = 40 }) {
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: `${color}26`, color, border: `1px solid ${color}55` }}>
      <Icon sx={{ fontSize: size * 0.55 }} aria-hidden />
    </Avatar>
  );
}

export function ChangeChip({ pct, label }) {
  const s = useSurface();
  if (pct === null || pct === undefined) {
    return (
      <Chip size="small" icon={<RemoveIcon />} label={`— ${label}`}
        sx={{ fontSize: 12, height: 24, color: s.muted, bgcolor: s.hover, "& .MuiChip-icon": { color: s.muted } }} />
    );
  }
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? s.muted : up ? PALETTE.green : PALETTE.red;
  const Icon = flat ? RemoveIcon : up ? ArrowDropUpIcon : ArrowDropDownIcon;
  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={`${flat ? "No change" : `${up ? "Up" : "Down"} ${Math.abs(pct)}%`} ${label}`}
      sx={{ fontSize: 12, height: 24, fontWeight: 700, color, bgcolor: `${color}1f`, "& .MuiChip-icon": { color } }}
    />
  );
}

/** KPI card: icon, number, label, optional % chip and one short caption. */
export function KpiCard({ label, Icon, color, state, reason, value, change, caption, onClick, info }) {
  const s = useSurface();
  return (
    <Paper
      elevation={0}
      onClick={state === "ready" ? onClick : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      sx={{
        p: 2.25, height: "100%", borderRadius: 2, backgroundColor: s.card, border: `1px solid ${s.border}`,
        borderTop: `3px solid ${color}`, cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { backgroundColor: s.hover } : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconBubble Icon={Icon} color={color} />
        <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: s.muted }}>{label}</Typography>
          {info}
        </Box>
      </Box>
      {state === "loading" && <Skeleton width="55%" height={48} sx={{ mt: 1 }} />}
      {(state === "error" || state === "unavailable") && (
        <>
          <Typography sx={{ fontSize: 32, fontWeight: 800, mt: 1, color: s.muted }} aria-label="Not available">—</Typography>
          <Typography sx={{ fontSize: 13, color: s.muted }}>
            {state === "error" ? "Could not load." : UNAVAILABLE_TEXT[reason] || "Not available."}
          </Typography>
        </>
      )}
      {state === "ready" && (
        <>
          <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1.15, mt: 1.25, color: s.text, wordBreak: "break-word" }}>
            {value}
          </Typography>
          {change && <Box sx={{ mt: 1 }}>{change}</Box>}
          {caption && <Typography sx={{ fontSize: 13, color: s.muted, mt: 1 }}>{caption}</Typography>}
        </>
      )}
    </Paper>
  );
}

/** Dark tooltip box used by every chart (readable in both themes). */
export function TooltipBox({ title, rows }) {
  return (
    <Box sx={{ bgcolor: "#16202e", color: "#fff", border: "1px solid #3a4658", borderRadius: 1,
      px: 1.5, py: 1, minWidth: 150 }}>
      {title && <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>{title}</Typography>}
      {rows.map((row) => (
        <Box key={row.label} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13 }}>
          {row.color && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: row.color, flexShrink: 0 }} />}
          <Typography component="span" sx={{ fontSize: 13, color: "#cfd8e3", flex: 1 }}>{row.label}</Typography>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{row.value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Donut with a centre label and a legend listing value and share. ``items``: {key,label,color,value,display,Icon}. */
export function Donut({ items, centerValue, centerLabel, onSelect, height = 200 }) {
  const s = useSurface();
  const total = items.reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  const shown = items.filter((i) => Number(i.value) > 0);
  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row", md: "column", xl: "row" }, gap: 2, alignItems: "center" }}>
      <Box sx={{ position: "relative", width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={shown.length ? shown : [{ key: "none", value: 1 }]} dataKey="value" nameKey="label"
              innerRadius="68%" outerRadius="96%" paddingAngle={shown.length > 1 ? 2 : 0} stroke="none"
              isAnimationActive={false}
              onClick={onSelect ? (entry) => entry?.key && entry.key !== "none" && onSelect(entry.key) : undefined}>
              {(shown.length ? shown : [{ key: "none" }]).map((entry) => (
                <Cell key={entry.key} fill={entry.key === "none" ? s.grid : entry.color}
                  style={{ cursor: onSelect ? "pointer" : "default", outline: "none" }} />
              ))}
            </Pie>
            {shown.length > 0 && (
              <ChartTooltip content={({ active, payload }) => (active && payload?.length ? (
                <TooltipBox title={payload[0].payload.label} rows={[
                  { label: "Value", value: payload[0].payload.display, color: payload[0].payload.color },
                  { label: "Share", value: `${total ? ((payload[0].payload.value / total) * 100).toFixed(1) : 0}%` },
                ]} />
              ) : null)} />
            )}
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", pointerEvents: "none", textAlign: "center", px: 3 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.text, lineHeight: 1.2 }}>{centerValue}</Typography>
          <Typography sx={{ fontSize: 12, color: s.muted }}>{centerLabel}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
        {items.map((item) => {
          const pct = total ? ((Number(item.value) || 0) / total) * 100 : 0;
          const ItemIcon = item.Icon;
          return (
            <Box key={item.key} onClick={onSelect ? () => onSelect(item.key) : undefined}
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, borderBottom: `1px solid ${s.border}`,
                cursor: onSelect ? "pointer" : "default" }}>
              {ItemIcon
                ? <ItemIcon sx={{ fontSize: 18, color: item.color }} aria-hidden />
                : <Box sx={{ width: 12, height: 12, borderRadius: "4px", bgcolor: item.color, flexShrink: 0 }} />}
              <Typography sx={{ fontSize: 14, color: s.text, flex: 1, minWidth: 0 }} noWrap>{item.label}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text }}>{item.display}</Typography>
              <Typography sx={{ fontSize: 13, color: s.muted, minWidth: 48, textAlign: "right" }}>{pct.toFixed(1)}%</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const STATUS_ICONS = {
  active: CheckCircleOutlineIcon, frozen: AcUnitIcon, expired: ErrorOutlineIcon, grace_period: ErrorOutlineIcon,
  pending: HourglassEmptyIcon, under_review: HourglassEmptyIcon, suspended: BlockIcon, cancelled: BlockIcon,
  inactive: BlockIcon, blacklisted: BlockIcon,
};

export function StatusBadge({ status, blocked }) {
  const s = useSurface();
  const meta = MEMBER_STATUS_META[status] || { label: membershipStatusLabel(status) || titleCase(status), color: PALETTE.grey };
  const Icon = STATUS_ICONS[status] || HelpOutlineIcon;
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon sx={{ fontSize: 16, color: `${meta.color} !important` }} />}
      label={`${meta.label}${blocked ? " · not eligible" : ""}`}
      sx={{ borderColor: meta.color, color: s.text, fontWeight: 600, fontSize: 12.5 }}
    />
  );
}

export const gridSx = (s) => ({
  border: "none",
  fontSize: 13,
  "& .MuiDataGrid-cell": { borderColor: s.border, display: "flex", alignItems: "center", fontSize: 13 },
  "& .name-column--cell": { color: s.colors.greenAccent[s.dark ? 300 : 600], fontWeight: 600 },
  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
    backgroundColor: s.dark ? s.colors.blueAccent[700] : s.colors.blueAccent[800],
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: 13 },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: s.card },
  "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${s.border}`, fontSize: 13 },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover": { backgroundColor: s.hover },
  "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 13 },
});

/**
 * Right-hand drawer with a server-paged (or client-paged) table and optional search.
 * ``fetchPage({ page, pageSize, search })`` resolves to ``{ rows, total, summary }``.
 * With ``tabs`` ([{ key, label, columns, fetchPage, emptyText, searchPlaceholder }]) each tab has its own list.
 */
export function ListDrawer({ tabs, ...props }) {
  const [tab, setTab] = useState(0);
  const { open } = props;
  useEffect(() => { if (open) setTab(0); }, [open, tabs]);
  if (!tabs || !tabs.length) return <DrawerList {...props} />;
  const active = tabs[Math.min(tab, tabs.length - 1)];
  return (
    <DrawerList
      {...props}
      columns={active.columns}
      fetchPage={active.fetchPage}
      emptyText={active.emptyText}
      searchPlaceholder={active.searchPlaceholder || props.searchPlaceholder}
      header={(
        <Tabs value={Math.min(tab, tabs.length - 1)} onChange={(e, value) => setTab(value)}
          textColor="inherit" sx={{ borderBottom: 1, borderColor: "divider" }}>
          {tabs.map((t) => <Tab key={t.key} label={t.label} sx={{ textTransform: "none", fontSize: 14, fontWeight: 700 }} />)}
        </Tabs>
      )}
    />
  );
}

function DrawerList({ open, onClose, title, subtitle, columns, fetchPage, searchable, searchPlaceholder,
  getRowId, onRowClick, footer, emptyText, header }) {
  const s = useSurface();
  const [model, setModel] = useState({ page: 0, pageSize: 25 });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [state, setState] = useState({ rows: [], total: 0, summary: null, loading: false, error: "" });
  const latest = useRef(0);

  useEffect(() => {
    if (!open) return;
    setModel((m) => ({ ...m, page: 0 }));
    setSearchInput("");
    setSearch("");
  }, [open, fetchPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setModel((m) => ({ ...m, page: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!open || !fetchPage) return;
    const id = ++latest.current;
    setState((st) => ({ ...st, loading: true, error: "" }));
    try {
      const result = await fetchPage({ page: model.page + 1, pageSize: model.pageSize, search });
      if (id !== latest.current) return;
      setState({ ...result, loading: false, error: "" });
    } catch (e) {
      if (id !== latest.current) return;
      setState({ rows: [], total: 0, summary: null, loading: false, error: requestError(e, "Could not load this list.") });
    }
  }, [open, fetchPage, model, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 600, md: 760 }, backgroundColor: s.card, backgroundImage: "none" } }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.text }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: 14, color: s.muted }}>{subtitle}</Typography>}
          </Box>
          <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
        </Box>
        {header}
        {state.summary && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {state.summary.map((item) => (
              <Chip key={item} label={item} sx={{ fontSize: 13, fontWeight: 700 }} />
            ))}
          </Box>
        )}
        {searchable && (
          <TextField size="small" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder={searchPlaceholder || "Search"} inputProps={{ "aria-label": searchPlaceholder || "Search" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        )}
        {state.error && <Alert severity="error" variant="outlined">{state.error}</Alert>}
        <Divider />
        <Box sx={{ flex: 1, minHeight: 320, minWidth: 0 }}>
          <DataGrid
            rows={state.rows}
            columns={columns.map((column) => ({ ...column, sortable: false }))}
            getRowId={getRowId}
            rowCount={state.total}
            loading={state.loading}
            paginationMode="server"
            paginationModel={model}
            onPaginationModelChange={setModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            onRowClick={onRowClick}
            localeText={{ noRowsLabel: emptyText || "Nothing to show" }}
            sx={gridSx(s)}
          />
        </Box>
        {footer && <Box>{footer}</Box>}
        <Button onClick={onClose} variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none" }}>Close</Button>
      </Box>
    </Drawer>
  );
}

/** Minimal client-side paging for lists the API returns in full. */
export const pageLocally = (rows, { page, pageSize, search }, matches) => {
  const filtered = search ? rows.filter((row) => matches(row, search.toLowerCase())) : rows;
  return { rows: filtered.slice((page - 1) * pageSize, page * pageSize), total: filtered.length };
};
